import * as React from "react"
import {useCallback, useEffect, useRef} from "react"
import {observer} from "mobx-react"
import {Tab, TabBar} from "@cling/lib.web.mdc/tab_bar"
import {i18n} from "@cling/lib.web.i18n"
import {SearchableList} from "@cling/lib.web.mdc/searchable_list"
import {running_on_mobile_device} from "@cling/lib.web.utils"
import {goto_board, goto_dashboard, with_shortcut} from "../utils"
import {board_chooser_items} from "@cling/lib.web.board_chooser"
import {
    board_chooser_props,
    people_chooser_board_infos,
    people_chooser_props,
    board_chooser_board_infos,
} from "./board_chooser_props"
import {
    AccessLevel,
    board_uid_type,
    BoardType,
    PatchFullAccountRequest,
    create_PatchUID,
    PatchAccountSettings,
    TriState,
} from "@cling/lib.shared.model"
import {reaction, runInAction} from "mobx"
import {ui_state, ui_actions, current_user} from "../state/index"
import {report_error} from "@cling/lib.shared.debug"
import {classNames} from "@cling/lib.web.utils"
import {Button} from "@cling/lib.web.mdc/button"
import {IconButton} from "@cling/lib.web.mdc/icon_button"
import {GlobalBackdrop} from "../misc/global_backdrop"
import {call_function} from "@cling/lib.shared.faas"
import {ToggleSortBoardsAlphabetically} from "./toggle_sort_boards_alphabetically"

export const DesktopBoardChooser = observer(() => {
    const [search_visible, set_search_visible] = React.useState(false)
    useEffect(() =>
        reaction(
            () => ui_state.current_board.board,
            (current_board) => {
                if (ui_state.desktop_board_chooser_state !== "hidden") {
                    searchable_list_ref.current?.reset_search()
                    if (current_board?.board_type === BoardType.people) {
                        ui_actions.toggle_desktop_board_chooser("people")
                    } else if (current_board) {
                        ui_actions.toggle_desktop_board_chooser("boards")
                    }
                }
            },
            {name: "desktop_board_chooser.tsx - current_board"},
        ),
    )
    const activate_people_tab = useCallback(() => {
        ui_actions.toggle_desktop_board_chooser("people")
        searchable_list_ref.current?.reset_search()
        searchable_list_ref.current?.reset_folded()
    }, [])
    const activate_boards_tab = useCallback(() => {
        ui_actions.toggle_desktop_board_chooser("boards")
        searchable_list_ref.current?.reset_search()
        searchable_list_ref.current?.reset_folded()
    }, [])
    const searchable_list_ref = useRef<SearchableList>(null)
    useEffect(() => {
        reaction(
            () => [
                ui_state.desktop_board_chooser_focus_search,
                ui_state.desktop_board_chooser_state,
            ],
            ([focus_search]) => {
                searchable_list_ref.current?.reset_search()
                if (focus_search) {
                    set_search_visible(true)
                    searchable_list_ref.current?.focus_search_field()
                    ui_state.desktop_board_chooser_focus_search = false
                }
            },
            {name: "desktop_board_chooser.tsx - desktop_board_chooser_focus_search"},
        )
    }, [])
    const goto_board_ = useCallback((board_uid) => {
        if (current_user.account_settings.auto_close_board_chooser) {
            ui_actions.toggle_desktop_board_chooser("hidden")
        }
        const board_type = board_uid_type(board_uid)
        if (!running_on_mobile_device() && board_type === BoardType.clipboard) {
            ui_actions.toggle_clipboard_shown()
        } else if (!running_on_mobile_device() && board_type === BoardType.trashcan) {
            ui_actions.toggle_trashcan_shown()
        } else if (board_type === BoardType.dashboard) {
            goto_dashboard().catch(report_error)
        } else {
            goto_board({board_uid}).catch(report_error)
        }
    }, [])
    const on_toggle_search = useCallback(
        () => set_search_visible(!search_visible),
        [search_visible],
    )
    useEffect(() => {
        if (search_visible) {
            searchable_list_ref.current?.focus_search_field()
        }
    }, [search_visible])
    const {auto_close_board_chooser: auto_close, sort_boards_alphabetically} =
        current_user.account_settings
    const on_auto_close_change = useCallback(() => {
        runInAction(() => (current_user.account_settings.auto_close_board_chooser = !auto_close))
        call_function(
            new PatchFullAccountRequest({
                patch_uid: create_PatchUID(),
                patch_account_settings: new PatchAccountSettings({
                    auto_close_board_chooser: auto_close ? TriState.false : TriState.true,
                }),
            }),
        ).catch(report_error)
    }, [auto_close])
    const add_board = useCallback(() => {
        if (ui_state.desktop_board_chooser_state === "boards") {
            ui_actions.open_add_board_dialog()
        } else {
            ui_actions.open_start_conversation_dialog()
        }
    }, [])
    const close = useCallback(() => ui_actions.toggle_desktop_board_chooser("hidden"), [])
    const tab = ui_state.desktop_board_chooser_state === "people" ? "people" : "boards"
    const people_board_badge = people_chooser_board_infos().some((x) =>
        ui_state.is_new_or_changed(x),
    )
        ? badge
        : undefined
    const board_badge = board_chooser_board_infos("user_boards", AccessLevel.read).some(
        (x) => x.is_user_board && ui_state.is_new_or_changed(x),
    )
        ? badge
        : undefined
    const items =
        tab === "boards"
            ? board_chooser_items({
                  element_type: "ListItem",
                  sort_boards_alphabetically,
                  ...board_chooser_props("user_boards", AccessLevel.read),
              })
            : board_chooser_items({
                  element_type: "ListItem",
                  sort_boards_alphabetically,
                  ...people_chooser_props(),
              })
    return (
        <nav
            key={current_user.account.locale}
            className={classNames("desktop-board-chooser", {
                "desktop-board-chooser--open": ui_state.desktop_board_chooser_state !== "hidden",
            })}
        >
            <GlobalBackdrop />
            <div className="desktop-board-chooser__add-board">
                <Button
                    onClick={add_board}
                    icon="edit"
                    data-test-id="DesktopBoardChooser_add_board"
                    raised
                >
                    {tab === "boards" ? i18n.add_a_board : i18n.start_a_conversation}
                </Button>
            </div>
            <TabBar className="desktop-board-chooser__tab-bar" disable_focus_on_activate>
                <Tab
                    tooltip={with_shortcut(i18n.boards, "b")}
                    selected={tab === "boards"}
                    icon="dashboard"
                    label={
                        <div className="desktop-board-chooser__tab">
                            {board_badge}
                            {i18n.boards}
                        </div>
                    }
                    onClick={activate_boards_tab}
                />
                <Tab
                    tooltip={with_shortcut(i18n.people, "p")}
                    selected={tab === "people"}
                    icon="face"
                    label={
                        <div className="desktop-board-chooser__tab">
                            {people_board_badge}
                            {i18n.people}
                        </div>
                    }
                    onClick={activate_people_tab}
                />
            </TabBar>
            <SearchableList
                ref={searchable_list_ref}
                className="desktop-board-chooser__list"
                data-test-id="DesktopBoardChooser_boards"
                search_label={i18n.search}
                onSelected={goto_board_}
                toggle_fold_button_caption={i18n.toggle_fold_boards}
                search_visible={search_visible}
                search_pos="bottom"
            >
                {items}
            </SearchableList>
            <div key="top_buttons">
                <div className="desktop-board-chooser__top-buttons">
                    <IconButton
                        icon={search_visible ? "search_off" : "search"}
                        onClick={on_toggle_search}
                        outlined
                        small
                        tooltip={`${
                            tab === "people" ? i18n.search_for_a_person : i18n.search_for_a_board
                        }   ${tab === "people" ? "p" : "b"}`}
                    />
                    <ToggleSortBoardsAlphabetically display="icon" />
                    <IconButton
                        icon="push_pin"
                        small
                        onClick={on_auto_close_change}
                        outlined={auto_close}
                        tooltip={auto_close ? i18n.always_close : i18n.keep_open}
                    />
                    <IconButton
                        icon="close"
                        small
                        onClick={close}
                        data-test-id="DesktopBoardChooser_close"
                    />
                </div>
            </div>
            {!current_user.account_attributes.has_pro_features &&
                // Needed for Obersee.
                current_user.account_attributes.team_uids.length == 0 && (
                    <div className="desktop-board-chooser__try-cling-pro full-width flex-col align-items--center">
                        <Button raised secondary onClick={ui_actions.open_checkout_dialog}>
                            {i18n.try_cling_pro}
                        </Button>
                        <div className="text-align--center mt--1">{i18n.get_all_the_space}</div>
                    </div>
                )}
        </nav>
    )
})

const badge = (
    <div className="desktop-board-chooser__badge mdcx-badge mdcx-badge--small mdcx-badge--secondary" />
)
